import * as Sentry from '@sentry/react';
import { config } from './config/env';
import type { User } from './store/useAuthStore';
import { useAuthStore } from './store/useAuthStore';

declare global {
  interface Window {
    __SENTRY_FETCH_INSTRUMENTED__?: boolean;
  }
}

const parseSampleRate = (
  value: string | undefined,
  fallback: number
): number => {
  if (!value) {
    return fallback;
  }

  const parsedValue = Number(value);
  if (!Number.isFinite(parsedValue) || parsedValue < 0 || parsedValue > 1) {
    return fallback;
  }

  return parsedValue;
};

const sentryDsn = import.meta.env.VITE_SENTRY_DSN;
const sentryRelease =
  import.meta.env.VITE_SENTRY_RELEASE ||
  import.meta.env.REACT_APP_SENTRY_RELEASE ||
  'production';
const isSentryEnabled = config.NODE_ENV === 'production' && Boolean(sentryDsn);

const shouldCaptureApiUrl = (input: RequestInfo | URL): boolean => {
  const baseApiUrl = config.API_URL;
  if (!baseApiUrl) {
    return false;
  }

  const requestUrl =
    typeof input === 'string'
      ? input
      : input instanceof URL
        ? input.toString()
        : input.url;

  return requestUrl.startsWith(baseApiUrl);
};

const normalizeError = (error: unknown): Error => {
  if (error instanceof Error) {
    return error;
  }

  return new Error(String(error));
};

const setupGlobalErrorCapture = (): void => {
  if (typeof window === 'undefined') {
    return;
  }

  window.addEventListener('error', event => {
    const error = event.error ?? new Error(event.message);
    Sentry.captureException(error, {
      tags: { source: 'window.onerror' },
      extra: {
        filename: event.filename,
        lineno: event.lineno,
        colno: event.colno,
      },
    });
  });

  window.addEventListener('unhandledrejection', event => {
    Sentry.captureException(normalizeError(event.reason), {
      tags: { source: 'window.onunhandledrejection' },
    });
  });
};

const setupFetchCapture = (): void => {
  if (typeof window === 'undefined' || window.__SENTRY_FETCH_INSTRUMENTED__) {
    return;
  }

  const originalFetch = window.fetch.bind(window);
  window.fetch = async (input: RequestInfo | URL, init?: RequestInit) => {
    try {
      const response = await originalFetch(input, init);

      if (!response.ok && shouldCaptureApiUrl(input)) {
        Sentry.captureMessage('API request failed', {
          level: 'error',
          tags: { source: 'fetch', type: 'http_error' },
          extra: {
            url: response.url,
            status: response.status,
            statusText: response.statusText,
            method: init?.method ?? 'GET',
          },
        });
      }

      return response;
    } catch (error) {
      if (shouldCaptureApiUrl(input)) {
        Sentry.captureException(normalizeError(error), {
          tags: { source: 'fetch', type: 'network_error' },
          extra: {
            url:
              typeof input === 'string'
                ? input
                : input instanceof URL
                  ? input.toString()
                  : input.url,
            method: init?.method ?? 'GET',
          },
        });
      }

      throw error;
    }
  };

  window.__SENTRY_FETCH_INSTRUMENTED__ = true;
};

if (isSentryEnabled) {
  Sentry.init({
    dsn: sentryDsn,
    environment: config.NODE_ENV,
    sendDefaultPii: true,
    integrations: [Sentry.replayIntegration()],
    release: sentryRelease,
    replaysSessionSampleRate: parseSampleRate(
      import.meta.env.VITE_SENTRY_REPLAY_SESSION_SAMPLE_RATE,
      0.1
    ),
    replaysOnErrorSampleRate: parseSampleRate(
      import.meta.env.VITE_SENTRY_REPLAY_ON_ERROR_SAMPLE_RATE,
      1.0
    ),
    ignoreErrors: ['ResizeObserver loop limit exceeded'],
    beforeSend(event) {
      try {
        const authState = useAuthStore.getState();

        if (authState.user) {
          event.user = {
            id: String(authState.user.id),
            email: authState.user.email,
            username: authState.user.name || authState.user.email,
          };

          event.tags = {
            ...event.tags,
            userId: String(authState.user.id),
            isLoggedIn: String(authState.isLoggedIn),
          };

          event.contexts = {
            ...event.contexts,
            auth: {
              user: {
                id: authState.user.id,
                email: authState.user.email,
                name: authState.user.name,
                permissions: authState.user.permissions || [],
              },
              isLoggedIn: authState.isLoggedIn,
              isLoading: authState.isLoading,
              hasUserProducts: authState.userProducts.length > 0,
              hasUserCourses: authState.userCourses.length > 0,
              userInfo: authState.userInfo
                ? {
                    id: authState.userInfo.id,
                    email: authState.userInfo.email,
                  }
                : null,
            },
          };
        }
      } catch (error) {
        console.warn('Failed to add auth context to Sentry event:', error);
      }

      return event;
    },
  });

  setupGlobalErrorCapture();
  setupFetchCapture();

  try {
    let lastUserId: number | null = null;

    useAuthStore.subscribe(
      (state: { user: User | null }) => state.user,
      (user: User | null) => {
        if (user && user.id !== lastUserId) {
          Sentry.setUser({
            id: String(user.id),
            email: user.email,
            username: user.name || user.email,
          });
          lastUserId = user.id;
        } else if (!user) {
          Sentry.setUser(null);
          lastUserId = null;
        }
      }
    );
  } catch (error) {
    console.warn('Failed to setup Sentry user subscription:', error);
  }
}
